import { useMemo } from 'react'
import { Box, Link, Typography } from '@mui/material'
import { PRIVACY_NOTICE, toPrivacyNoticeTextRuns } from 'pure-js/libs/privacyNotice'
import {
  PrivacyNoticeBlockKind,
  type PrivacyNoticeBlock,
  type PrivacyNoticeSection,
  type PrivacyNoticeTextRun
} from 'pure-js/types/PrivacyNotice'
import {
  contactLinkSx,
  emphasisSx,
  lastUpdatedSx,
  listItemSx,
  listSx,
  paragraphSx,
  sectionSx,
  sectionTitleSx,
  subheadingSx
} from './privacyNoticeStyles'

export function PrivacyNoticeContent() {
  return (
    <Box>
      <Typography sx={lastUpdatedSx}>{PRIVACY_NOTICE.lastUpdated}</Typography>
      {PRIVACY_NOTICE.introduction.map((block, index) => (
        <NoticeBlock key={index} block={block} />
      ))}
      {PRIVACY_NOTICE.sections.map((section) => (
        <NoticeSection key={section.title} section={section} />
      ))}
    </Box>
  )
}

function NoticeSection({ section }: { section: PrivacyNoticeSection }) {
  return (
    <Box component="section" sx={sectionSx}>
      <Typography component="h3" sx={sectionTitleSx}>
        {section.title}
      </Typography>
      {section.blocks.map((block, index) => (
        <NoticeBlock key={index} block={block} />
      ))}
    </Box>
  )
}

function NoticeBlock({ block }: { block: PrivacyNoticeBlock }) {
  switch (block.kind) {
    case PrivacyNoticeBlockKind.SUBHEADING:
      return (
        <Typography component="h4" sx={subheadingSx}>
          {block.text}
        </Typography>
      )
    case PrivacyNoticeBlockKind.LIST:
      return (
        <Box component="ul" sx={listSx}>
          {block.items.map((item) => (
            <NoticeListItem key={item} text={item} />
          ))}
        </Box>
      )
    case PrivacyNoticeBlockKind.CONTACT:
      return (
        <Box>
          <Link href={`mailto:${block.email}`} sx={contactLinkSx}>
            {block.email}
          </Link>
        </Box>
      )
    case PrivacyNoticeBlockKind.PARAGRAPH:
      return (
        <Typography sx={paragraphSx}>
          <EmphasizedText text={block.text} />
        </Typography>
      )
  }
}

function NoticeListItem({ text }: { text: string }) {
  return (
    <Typography component="li" sx={listItemSx}>
      <EmphasizedText text={text} />
    </Typography>
  )
}

function EmphasizedText({ text }: { text: string }) {
  const runs = useMemo(() => toPrivacyNoticeTextRuns(text), [text])

  return (
    <>
      {runs.map((run, index) => (
        <TextRun key={index} run={run} />
      ))}
    </>
  )
}

function TextRun({ run }: { run: PrivacyNoticeTextRun }) {
  if (!run.isEmphasized) return <>{run.text}</>

  return (
    <Box component="strong" sx={emphasisSx}>
      {run.text}
    </Box>
  )
}
